/**
 * Built-in framework data: time, science, secondLanguage, selfCare.
 *
 * Authoring rules used throughout this file:
 *  - Every level descriptor names a behavior a parent can watch happen. If you
 *    cannot verify it in the moment, it does not belong in a descriptor.
 *  - Descriptors are written without pronouns. The framework is shared across
 *    children; the child's own pronouns live on the Child record.
 *  - Growth framing only. Level 1 is a real accomplishment, never a lack.
 *  - `typicalMonths` is the band in which level 3 usually appears, drawn from
 *    developmental norms (Piaget on sequence and duration, Montessori practical
 *    life sequences, standard toileting/dressing/hygiene milestones). It is
 *    guidance for planning, never a verdict.
 *  - secondLanguage skills carry no `typicalMonths` on purpose: progress there
 *    tracks hours of exposure, not age. A child hearing a language two days a
 *    month and one hearing it daily are on different clocks, and stamping an
 *    age band on it would be false precision.
 */

import type {
  DomainDef,
  DomainId,
  DomainKey,
  FrameworkId,
  Level,
  LevelDescriptor,
  Skill,
  SkillId,
} from '../types'

// ─────────────────────────────────────────────────────────────
// Ids
// ─────────────────────────────────────────────────────────────

/** Matches the id used by the sibling built-in framework files. */
const FRAMEWORK_ID = 'framework.aurora.builtin.v1' as FrameworkId

const domainId = (key: DomainKey): DomainId => `domain.${key}` as DomainId

const skillId = (domainKey: DomainKey, key: string): SkillId =>
  `${domainKey}.${key}` as SkillId

// ─────────────────────────────────────────────────────────────
// Authoring helpers (pure)
// ─────────────────────────────────────────────────────────────

interface LevelSeed {
  descriptor: string
  exemplars: string[]
}

interface SkillSeed {
  key: string
  name: string
  description: string
  typicalMonths?: readonly [number, number]
  /** Skill keys inside the same domain. Only real blockers. */
  prerequisiteKeys?: string[]
  assessmentHints: string[]
  /** Exactly five, in level order 1..5. */
  levels: readonly [LevelSeed, LevelSeed, LevelSeed, LevelSeed, LevelSeed]
}

const toLevelDescriptors = (levels: readonly LevelSeed[]): LevelDescriptor[] =>
  levels.map((seed, index) => ({
    level: (index + 1) as Level,
    descriptor: seed.descriptor,
    exemplars: seed.exemplars,
  }))

const buildSkills = (domainKey: DomainKey, seeds: readonly SkillSeed[]): Skill[] =>
  seeds.map((seed, index) => ({
    _id: skillId(domainKey, seed.key),
    domainId: domainId(domainKey),
    domainKey,
    key: seed.key,
    name: seed.name,
    description: seed.description,
    levelDescriptors: toLevelDescriptors(seed.levels),
    ...(seed.typicalMonths ? { typicalMonths: seed.typicalMonths } : {}),
    prerequisites: (seed.prerequisiteKeys ?? []).map((key) => skillId(domainKey, key)),
    assessmentHints: seed.assessmentHints,
    sortOrder: index + 1,
  }))

const buildDomain = (
  key: DomainKey,
  name: string,
  description: string,
  iconKey: string,
  sortOrder: number,
): DomainDef => ({
  _id: domainId(key),
  frameworkId: FRAMEWORK_ID,
  key,
  name,
  description,
  colorToken: `domain.${key}`,
  iconKey,
  sortOrder,
})

// ─────────────────────────────────────────────────────────────
// TIME
// ─────────────────────────────────────────────────────────────

export const TIME_DOMAIN: DomainDef = buildDomain(
  'time',
  'Time Concepts',
  'Time is invisible, so a child builds it out of routines, repeated markers and words: parts of the day, before and after, sleeps until, days on a calendar, hands on a clock.',
  'clock',
  10,
)

const TIME_SEEDS: readonly SkillSeed[] = [
  {
    key: 'dayNightCycle',
    name: 'Parts of the Day',
    description:
      'Naming morning, afternoon, evening and night, and tying each one to what happens then and to what the sky looks like.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'At the window during a routine moment, ask "how do you know it is morning?" and listen for a sky cue or a routine cue.',
      'Lay out four photos of the day (breakfast, park, dinner, bath) and ask which one goes with a dark sky.',
      'At dinner ask "what part of the day is it now, and what comes next?"',
    ],
    levels: [
      {
        descriptor:
          'Responds to the routine words for times of day by moving into the routine — heads for the bedroom at "bedtime", goes to the table at "dinner time" — without naming the part of day.',
        exemplars: [
          'Says "night night" while carrying a blanket to bed',
          'Walks to the high chair when an adult says "breakfast"',
        ],
      },
      {
        descriptor:
          'Names day and night correctly from the sky or a picture, and sorts two pictures (sunny playground, dark bedroom) into day and night.',
        exemplars: [
          'Points out the window and says "dark, night time"',
          'Puts the moon card with the pajamas card',
        ],
      },
      {
        descriptor:
          'Names morning, afternoon, evening and night, and pairs each one with a routine that happens then.',
        exemplars: [
          '"Morning is when we have breakfast, night is when we sleep"',
          'Sorts six routine pictures into the four parts of the day',
        ],
      },
      {
        descriptor:
          'Uses the part-of-day words unprompted to plan and to place events, including for a day that has not happened yet.',
        exemplars: [
          '"After nap it is afternoon, then we go to the park"',
          '"We saw Grandma in the evening, it was dark already"',
        ],
      },
      {
        descriptor:
          'Explains day and night as the Earth turning toward and away from the sun, and demonstrates it with a ball and a flashlight or with hands.',
        exemplars: [
          'Turns a globe and says "now our side is away from the sun, so it is night here"',
          'Uses "noon" and "midnight" in the right places',
        ],
      },
    ],
  },
  {
    key: 'beforeAfter',
    name: 'Before and After',
    description:
      'Putting events in order and using the words that mark the order. This is the seed of every other time concept.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Ask "what do we do before we eat?" during a routine that is already running.',
      'Hand over three photos from the morning and ask for them in order, first to last.',
      'Tell a two-step plan out of order ("we will put shoes on after we get the ball") and see whether the order is followed correctly.',
    ],
    levels: [
      {
        descriptor:
          'Anticipates the next step of a familiar routine by moving to it — brings the toothbrush after pajamas go on — without using order words.',
        exemplars: [
          'Fetches the bath towel as the water starts running',
          'Holds up both arms when the shirt comes off for the bath',
        ],
      },
      {
        descriptor:
          'Puts two pictures of a familiar routine in order, and uses "first" or "then" for at least one everyday sequence.',
        exemplars: [
          'Orders the wet-hands card and the dry-hands card',
          '"First shoes, then park"',
        ],
      },
      {
        descriptor:
          'Orders three or four steps of a daily routine and answers "what comes before / after ___?" for familiar events.',
        exemplars: [
          'Puts wake up, breakfast, park, nap in order',
          '"Bath comes before stories"',
        ],
      },
      {
        descriptor:
          'Uses before and after about events beyond the routine, including about the past, and catches an out-of-order retelling.',
        exemplars: [
          '"We got ice cream after the swings, not before"',
          'Objects when a story is retold with the ending first',
        ],
      },
      {
        descriptor:
          'Sequences a five or six step event and inserts a new step in the right place, explaining why it goes there.',
        exemplars: [
          '"The seed has to go in the dirt before we water it, or the water lands on nothing"',
          'Orders a six-card story and moves a card that was misplaced',
        ],
      },
    ],
  },
  {
    key: 'yesterdayTodayTomorrow',
    name: 'Yesterday, Today, Tomorrow',
    description:
      'Holding a day-sized chunk of time in mind: what already happened, what is happening, and what is coming. "How many sleeps" is the concrete handle.',
    typicalMonths: [30, 36],
    prerequisiteKeys: ['dayNightCycle', 'beforeAfter'],
    assessmentHints: [
      'At breakfast ask "what did we do yesterday?" and check whether the answer really is from yesterday.',
      'Before an exciting event, ask "how many sleeps until it?" and see whether the count changes correctly the next day.',
      'Ask "what are we doing tomorrow?" and listen for a future frame rather than a description of today.',
    ],
    levels: [
      {
        descriptor:
          'Talks about things that happened, with no time marker — the same words serve for this morning and last month.',
        exemplars: [
          '"We go to the beach" said about a trip weeks ago',
          'Uses "now" and "all done" to mark time',
        ],
      },
      {
        descriptor:
          'Marks past as different from now with one word, used broadly — "yesterday" covers any earlier day. This over-use is exactly how the concept starts.',
        exemplars: [
          '"Yesterday we saw the big dog" about something last week',
          'Uses "later" for anything still to come',
        ],
      },
      {
        descriptor:
          'Answers "what did we do yesterday?" and "what happens tomorrow?" with the right day most of the time, and counts one or two sleeps to a coming event.',
        exemplars: [
          'Names the actual visit that happened yesterday',
          '"Two sleeps until the cousins come"',
        ],
      },
      {
        descriptor:
          'Uses yesterday, today and tomorrow accurately along with "last night" and "the day after tomorrow", and updates a sleep count of four or five days each morning.',
        exemplars: [
          '"Three more sleeps now, it was four yesterday"',
          '"Last night I heard the rain"',
        ],
      },
      {
        descriptor:
          'Holds a week-sized timeline: links a count of days to a day name and an event, and talks about last week and next week as units.',
        exemplars: [
          '"Three more sleeps, that is Friday, the day Babushka comes"',
          '"We went to the aquarium last week, not this week"',
        ],
      },
    ],
  },
  {
    key: 'durationSense',
    name: 'How Long Things Take',
    description:
      'Feeling the length of a stretch of time and comparing one stretch to another. Duration is the hardest time concept for young children, so it is built with visible timers.',
    typicalMonths: [33, 42],
    assessmentHints: [
      'Run a one-minute sand timer during a wait and ask afterwards whether that felt long or short.',
      'Offer a choice: "does brushing teeth take about one minute or about ten minutes?" for three or four familiar activities.',
      'Ask for a guess before a timer starts — "how many jumps do you think you can do before the sand runs out?" — then count together.',
    ],
    levels: [
      {
        descriptor:
          'Waits a short moment when asked, with something to hold or watch; "soon" and "in a minute" are heard as "not right now".',
        exemplars: [
          'Waits at the door holding a toy while shoes are found',
          'Watches the microwave numbers change while food heats',
        ],
      },
      {
        descriptor:
          'Uses a visual timer: watches the sand or the ring run down and accepts the end of it as the signal to change activity.',
        exemplars: [
          'Stops the water play when the sand timer empties',
          'Says "all gone" when the timer finishes',
        ],
      },
      {
        descriptor:
          'Sorts familiar activities into quick and long, and sits through a one-minute timer while noticing it end.',
        exemplars: [
          '"Washing hands is quick, driving to Grandma is long"',
          'Holds a plank or a silly pose for a whole minute and calls the end',
        ],
      },
      {
        descriptor:
          'Estimates a short duration within a sensible range and checks the guess against a timer, and uses "a little while" and "a long time" to fit the case.',
        exemplars: [
          'Guesses twenty claps in ten seconds, counts fourteen, laughs at the difference',
          'Picks the right answer for four of five one-minute-or-ten-minute questions',
        ],
      },
      {
        descriptor:
          'Uses minutes to plan and compare, and senses a timer nearing its end without being told.',
        exemplars: [
          '"We have five minutes, that is enough for two songs"',
          '"The movie is longer than nap"',
        ],
      },
    ],
  },
  {
    key: 'daysOfWeek',
    name: 'Days of the Week',
    description:
      'The seven-day cycle as a named, ordered list, and then as a frame that holds real events.',
    typicalMonths: [42, 54],
    prerequisiteKeys: ['yesterdayTodayTomorrow'],
    assessmentHints: [
      'During the calendar ritual ask for today, then ask what tomorrow will be, without offering the song.',
      'Ask "what comes after Wednesday?" and notice whether the list restarts from Monday or continues from Wednesday.',
      'Ask which day a weekly event lands on — a class, a visit, pizza night.',
    ],
    levels: [
      {
        descriptor:
          'Joins the days-of-the-week song with the tune and a few landing words, and takes part in the daily calendar ritual.',
        exemplars: [
          'Sings the melody and lands on "Saturday"',
          'Puts the marker on the calendar square when handed it',
        ],
      },
      {
        descriptor:
          'Sings the whole song with most of the words, and recognizes one or two day names as belonging to particular events.',
        exemplars: [
          'Points to days on the calendar during the morning ritual',
          '"Saturday is when Papa stays home"',
        ],
      },
      {
        descriptor:
          'Names all seven days in order without the song, and points to today on the calendar.',
        exemplars: [
          'Recites Monday through Sunday unaided',
          'Finds today on the wall calendar and names it',
        ],
      },
      {
        descriptor:
          'Says what day it is and what tomorrow will be, and answers "what comes after ___?" starting from that day rather than from the top of the list.',
        exemplars: [
          '"Today is Tuesday, tomorrow is Wednesday"',
          'Answers "Thursday" to "what comes after Wednesday?" without restarting',
        ],
      },
      {
        descriptor:
          'Uses the week as a planning frame: knows which days carry which events, counts days forward to one, and separates weekdays from the weekend.',
        exemplars: [
          '"Three days until Saturday, then swimming"',
          '"On weekends nobody goes to work"',
        ],
      },
    ],
  },
  {
    key: 'clockAnchors',
    name: 'Clock Anchors',
    description:
      'Reading a few fixed times off a clock face as pictures of events — wake time, lunch time, bed time — long before reading the clock generally.',
    typicalMonths: [36, 48],
    prerequisiteKeys: ['dayNightCycle'],
    assessmentHints: [
      'Point at the clock near an anchor time and ask "what happens when it looks like this?"',
      'Draw an anchor time on a paper clock and ask which one it is.',
      'Ask "is it bedtime yet?" while the hour hand is a couple of numbers away and see what the answer rests on.',
    ],
    levels: [
      {
        descriptor:
          'Notices clocks, points at them and names them as clocks; may call the marks "numbers".',
        exemplars: [
          'Points at the kitchen clock and says "clock"',
          'Turns the hands of a toy clock in play',
        ],
      },
      {
        descriptor:
          'Matches a picture of the clock face at a familiar time to the routine picture when both are side by side.',
        exemplars: [
          'Puts the hands-straight-up card with the lunch card',
          'Finds the matching clock face out of two choices',
        ],
      },
      {
        descriptor:
          'Recognizes three or four anchor times on an analog clock and names the event that goes with each.',
        exemplars: [
          '"Both hands up top, that is lunch"',
          '"Little hand on eight, that is bed"',
        ],
      },
      {
        descriptor:
          'Checks the clock to answer a question about the routine and reasons from the hour hand about what has not happened yet.',
        exemplars: [
          '"Not bedtime, the little hand is not on eight yet"',
          'Looks at the clock and says "snack is soon"',
        ],
      },
      {
        descriptor:
          'Sets up a new anchor and tracks progress toward it by watching the hour hand move across the numbers.',
        exemplars: [
          'Puts a sticker on the 4 for "park time" and checks it twice',
          '"Two more numbers until we go"',
        ],
      },
    ],
  },
  {
    key: 'weeksMonths',
    name: 'Weeks and Months',
    description:
      'Bigger units than a day: the calendar grid as a picture of time, a week as seven sleeps, a month as many weeks, the year as named months.',
    typicalMonths: [48, 60],
    prerequisiteKeys: ['daysOfWeek'],
    assessmentHints: [
      'Count the squares on the calendar together to a real upcoming event and ask "is that more or less than a week?"',
      'Ask what month it is at the start of a month, then again a week later.',
      'Ask which month a birthday falls in and let the calendar be checked.',
    ],
    levels: [
      {
        descriptor:
          'Handles the calendar as an object — turns pages, points at squares — and hears "week" and "month" as words in the ritual.',
        exemplars: [
          'Turns the calendar page at the start of a month',
          'Pats the square for today',
        ],
      },
      {
        descriptor:
          'Knows the calendar is about days and marks off one square each day in the right place.',
        exemplars: [
          'Crosses off yesterday and finds today next to it',
          '"One box is one day"',
        ],
      },
      {
        descriptor:
          'Treats a week as seven sleeps: counts squares along a row to an event and uses "next week" for something past the coming weekend. Names the current month when reminded at the start of it.',
        exemplars: [
          'Counts five squares to the visit and says "not this week, next week"',
          'Answers "June" through the first week of June',
        ],
      },
      {
        descriptor:
          'Names the current month unprompted, names the birthday month, and reads the grid as weeks stacked into a month.',
        exemplars: [
          '"It is July, my birthday is in September"',
          'Counts four rows and says "four weeks in the month"',
        ],
      },
      {
        descriptor:
          'Names the twelve months in order, places events on the year, and works out "two weeks from now" on the calendar.',
        exemplars: [
          'Recites January through December',
          'Counts fourteen squares forward and lands on the right day',
        ],
      },
    ],
  },
  {
    key: 'seasons',
    name: 'Seasons',
    description:
      'The year as a repeating cycle, read from weather, clothing, trees and activities.',
    typicalMonths: [42, 54],
    assessmentHints: [
      'On a walk ask "how can you tell what season it is?" and listen for cues rather than the name alone.',
      'Lay out four pictures and ask for them in the order they come round.',
      'Ask what season comes after this one and what changes then.',
    ],
    levels: [
      {
        descriptor:
          'Notices the weather and connects it to clothing when prompted — reaches for a coat when told it is cold.',
        exemplars: [
          'Says "cold!" at the door and holds out arms for a jacket',
          'Points at rain on the window',
        ],
      },
      {
        descriptor:
          'Sorts summer and winter pictures — swimsuit against snowsuit — with a model to copy.',
        exemplars: [
          'Puts the sledge card with the snowman card',
          'Matches sandals to the beach picture',
        ],
      },
      {
        descriptor:
          'Names the current season and gives two cues for it, and names all four seasons when shown pictures.',
        exemplars: [
          '"It is summer, it is hot and the leaves are green"',
          'Names spring, summer, fall and winter from a four-picture set',
        ],
      },
      {
        descriptor:
          'Puts the four seasons in cycle order, says which one comes next, and names what changes then for a tree or an animal.',
        exemplars: [
          '"After fall is winter, the tree will be bare"',
          'Starts the cycle from summer instead of always from spring',
        ],
      },
      {
        descriptor:
          'Explains the year as a cycle that repeats and links seasons to months and to living things.',
        exemplars: [
          '"Leaves fall, then winter, then buds come back in spring, and it happens every year"',
          '"My birthday is in September, that is when fall starts"',
        ],
      },
    ],
  },
  {
    key: 'clockReading',
    name: 'Reading the Clock',
    description:
      'Reading an analog clock generally rather than by anchors: the hour hand, o’clock, half past, and matching a digital display.',
    typicalMonths: [60, 72],
    prerequisiteKeys: ['clockAnchors'],
    assessmentHints: [
      'Set a paper clock to an o’clock time and ask what time it says.',
      'Show a digital clock at 7:00 and ask for the same time to be made on the toy clock.',
      'Ask which hand tells the hour, and what the other one is doing.',
    ],
    levels: [
      {
        descriptor:
          'Points at the numbers on a clock face and counts some of them aloud.',
        exemplars: [
          'Counts "one, two, three" around the face',
          'Spins the hands and watches them move together',
        ],
      },
      {
        descriptor:
          'Names all twelve numbers on the face and moves the hands to where they are asked to go, without reading a time yet.',
        exemplars: [
          'Puts the long hand on the 12 when asked',
          'Finds the 9 on the clock face',
        ],
      },
      {
        descriptor:
          'Reads o’clock times: names the hour when the long hand points at the 12, for at least six different hours.',
        exemplars: [
          '"The little hand is on five and the big hand is up, so five o’clock"',
          'Reads 3:00, 6:00 and 11:00 correctly in a row',
        ],
      },
      {
        descriptor:
          'Reads o’clock and half past, and matches an analog clock to the digital display of the same time.',
        exemplars: [
          '"Half past seven, the big hand is at the bottom"',
          'Pairs the 7:30 digital card with the right clock face',
        ],
      },
      {
        descriptor:
          'Reads five-minute marks by counting by fives, or reads quarter past and quarter to, and works out what time it will be in one hour.',
        exemplars: [
          'Counts "5, 10, 15" round the face to read 2:15',
          '"It is four now, so after one hour it will be five"',
        ],
      },
    ],
  },
]

export const TIME_SKILLS: Skill[] = buildSkills('time', TIME_SEEDS)

// ─────────────────────────────────────────────────────────────
// SCIENCE
// ─────────────────────────────────────────────────────────────

export const SCIENCE_DOMAIN: DomainDef = buildDomain(
  'science',
  'Science & Nature',
  'Looking closely, guessing what will happen, testing it, and building a picture of how the living and physical world works. It doubles as the most natural vehicle for reasoning.',
  'flask-conical',
  11,
)

const SCIENCE_SEEDS: readonly SkillSeed[] = [
  {
    key: 'observation',
    name: 'Close Observation',
    description:
      'Looking at something long enough to describe details, notice change, and compare one specimen with another.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Hand over a magnifier and a leaf, then ask "what do you see that I cannot see from here?"',
      'Put two similar things side by side (two shells, two leaves) and ask for one difference and one sameness.',
      'Revisit the same plant or puddle two days running and ask what changed.',
    ],
    levels: [
      {
        descriptor:
          'Looks closely at small things, points them out, and carries interesting objects over to show an adult.',
        exemplars: [
          'Squats to watch an ant and points at it',
          'Brings a pinecone in from the walk',
        ],
      },
      {
        descriptor:
          'Names what is seen in one or two words and holds a magnifier over the object, whether or not it helps yet.',
        exemplars: ['"Bug!" while pointing at a beetle', '"Red leaf" while holding it up to the light'],
      },
      {
        descriptor:
          'Describes an object with two or more details after looking at it, and uses the magnifier to see rather than only to hold.',
        exemplars: [
          '"The beetle has six legs and shiny black wings"',
          'Bug hunt: reports "the ants are all going the same way"',
        ],
      },
      {
        descriptor:
          'Compares two specimens and names a difference and a sameness, and notices change in the same object over days.',
        exemplars: [
          '"This leaf is pointy, that one is round, but both have lines"',
          '"The leaf is browner than yesterday"',
        ],
      },
      {
        descriptor:
          'Keeps a record over several days — drawing or dictating what was seen — refers back to it, and asks a question about a detail nobody pointed out.',
        exemplars: [
          'Draws the sprout each day and compares the drawings',
          '"Why does the spider have lines on its legs?"',
        ],
      },
    ],
  },
  {
    key: 'prediction',
    name: 'Predicting',
    description:
      'Saying what will happen before it happens, then giving a reason, then revising the reason when the world disagrees.',
    typicalMonths: [33, 42],
    prerequisiteKeys: ['observation'],
    assessmentHints: [
      'Before dropping an object in water ask "will it sink or float? why do you think so?"',
      'Ask for a prediction on a second, similar object and see whether the first result gets used.',
      'After a surprising result, ask "what happened? did you think that would happen?"',
    ],
    levels: [
      {
        descriptor:
          'Anticipates a familiar physical outcome with the body — hands out under a pour, a flinch before the tower falls — without saying it.',
        exemplars: [
          'Cups hands under the jug before the water arrives',
          'Steps back as the block tower leans',
        ],
      },
      {
        descriptor:
          'Offers a guess when asked what will happen, without a reason yet; the guess may be the same whatever the object.',
        exemplars: [
          'Says "float" for every object in the sink-or-float tray',
          '"It will fall down"',
        ],
      },
      {
        descriptor:
          'Predicts before a familiar test and gives a reason based on one property that can be seen or felt.',
        exemplars: [
          '"It will float because it is light"',
          '"The rock will sink, it is heavy"',
        ],
      },
      {
        descriptor:
          'Uses the result of an earlier try to predict the next one, and says out loud when the outcome was not what was expected.',
        exemplars: [
          '"The little rock sank, so the big rock sinks too"',
          '"I thought it would sink but it floated!"',
        ],
      },
      {
        descriptor:
          'States a rule formed from several tries, then meets an exception and adjusts the rule rather than the memory of it.',
        exemplars: [
          '"Heavy things sink" — then the log floats — "so it is not heavy, it is what it is made of"',
          'Sorts new objects into two predicted piles before testing any',
        ],
      },
    ],
  },
  {
    key: 'experimentation',
    name: 'Running an Experiment',
    description:
      'Doing a test on purpose: setting it up, changing one thing, seeing what happens, and saying what was found.',
    typicalMonths: [33, 42],
    prerequisiteKeys: ['observation', 'prediction'],
    assessmentHints: [
      'Set out an experiment half-finished and ask "what should we try next?"',
      'After a test ask "what did you find out?" and listen for a result rather than a replay of the actions.',
      'Offer two ramps at different heights and see whether the same car gets used on both.',
    ],
    levels: [
      {
        descriptor:
          'Repeats an action to see the effect again, watching each time — the first form of a test.',
        exemplars: [
          'Drops the spoon from the chair again and again, watching it land',
          'Pours water out of the cup, refills it, pours again',
        ],
      },
      {
        descriptor:
          'Takes part in an experiment an adult has set up: pours, stirs, drops objects in, with the pleasure in the doing.',
        exemplars: [
          'Drops every object into the sink-or-float tub',
          'Stirs the food colouring into the water',
        ],
      },
      {
        descriptor:
          'Follows a two-step test alongside an adult and reports what happened afterwards.',
        exemplars: [
          'Colour mixing: "blue and yellow made green"',
          'Magnet hunt: "the spoon stuck, the cup did not"',
        ],
      },
      {
        descriptor:
          'Suggests the next thing to try and keeps something the same in order to compare — the same car on two ramps, the same water at two temperatures.',
        exemplars: [
          '"What if we use warm water?"',
          'Insists on rolling the same car down both ramps',
        ],
      },
      {
        descriptor:
          'Runs a fair test with light help: names what was changed and what was kept the same, repeats it to check, and states the finding.',
        exemplars: [
          '"I only changed the ramp, the car was the same, the high one won both times"',
          'Marks a tally of three tries on paper',
        ],
      },
    ],
  },
  {
    key: 'causeEffectNature',
    name: 'Cause and Effect in Nature',
    description:
      'Explaining why something in the world happened, chaining several steps, and reasoning backwards from an effect to its likely cause.',
    typicalMonths: [33, 42],
    prerequisiteKeys: ['observation'],
    assessmentHints: [
      'Point at something changed outdoors (a dried puddle, a fallen branch) and ask "what happened here?"',
      'Ask "and then what?" three times in a row to see how far the chain runs.',
      'Give an effect with two possible causes and ask which one it was and how to tell.',
    ],
    levels: [
      {
        descriptor:
          'Acts on the world to make an effect happen and watches for it — pushes the ball to see it roll, splashes to see the water jump.',
        exemplars: [
          'Rolls a ball down a slope repeatedly',
          'Stamps in a puddle and watches the splash',
        ],
      },
      {
        descriptor:
          'Answers "what happened?" with the immediate result of an event.',
        exemplars: ['"It fell down"', '"The water went everywhere"'],
      },
      {
        descriptor:
          'Explains why something in the world happened by naming one cause, using because or so.',
        exemplars: [
          '"The puddle is gone because the sun dried it"',
          '"The plant died so we did not water it"',
        ],
      },
      {
        descriptor:
          'Chains three or four steps in the right order, each one caused by the one before.',
        exemplars: [
          '"I left the ice cream out, it melted, it made a puddle, the floor got sticky"',
          '"Rain, then mud, then my boots got dirty, then Mama washed them"',
        ],
      },
      {
        descriptor:
          'Reasons backwards from an effect to two possible causes and chooses between them using another piece of evidence.',
        exemplars: [
          '"The ground is wet — rain or the sprinkler. The leaves are dry, so it was the sprinkler"',
          '"The bird flew away because it heard us, not because it was hungry"',
        ],
      },
    ],
  },
  {
    key: 'physicalProperties',
    name: 'Properties of Materials',
    description:
      'Heavy and light, rough and smooth, magnetic and not, solid and melted — the properties that make objects behave the way they do.',
    typicalMonths: [33, 42],
    prerequisiteKeys: ['observation'],
    assessmentHints: [
      'Put out five objects and ask for them in order from heaviest to lightest, then check by hand.',
      'Before a magnet touches an object, ask "will it stick? how do you know?"',
      'Leave an ice cube on a plate and come back to it twice, asking what is happening and why.',
    ],
    levels: [
      {
        descriptor:
          'Explores materials by touching, banging, squeezing and pouring, and reacts to hot, cold and wet.',
        exemplars: [
          'Pulls a hand back from a cold window and says "brr"',
          'Squeezes playdough and a sponge one after the other',
        ],
      },
      {
        descriptor:
          'Uses one or two property words correctly about the object in hand.',
        exemplars: ['"Heavy!" lifting a full jug', '"Wet!" after squeezing the sponge'],
      },
      {
        descriptor:
          'Sorts objects by a property that can be felt and names the property: heavy and light, rough and smooth, hard and soft, floats and sinks.',
        exemplars: [
          'Makes a rough pile and a smooth pile from eight objects and says why',
          'Describes a stone as "big and cold and hard"',
        ],
      },
      {
        descriptor:
          'Predicts behavior from a material before touching it, and knows water changes form — ice melts, puddles dry, steam rises.',
        exemplars: [
          '"That is metal so the magnet will stick and it will feel cold"',
          '"The ice turned into water because the room is warm"',
        ],
      },
      {
        descriptor:
          'Explains a property with a simple mechanism and uses it to predict a new case.',
        exemplars: [
          '"Magnets only pull some metals, not the coins"',
          '"The steeper ramp makes it go faster because it falls further"',
        ],
      },
    ],
  },
  {
    key: 'livingNonLiving',
    name: 'Living and Non-Living',
    description:
      'Sorting the world into what is alive and what is not, and moving from one test (it moves) to several (grows, feeds, has young).',
    typicalMonths: [36, 48],
    prerequisiteKeys: ['observation'],
    assessmentHints: [
      'Sort eight picture cards together and ask for the reason on each one, not just the pile.',
      'Offer the tricky cases — a tree, a car, a doll, a cloud — and listen for which test is being applied.',
      'Ask "what do all living things need?" and count the answers on fingers.',
    ],
    levels: [
      {
        descriptor:
          'Treats living things and objects alike in play, and shows care toward both; the sorting question has not opened yet.',
        exemplars: [
          'Feeds the toy dog and the real dog the same way',
          'Tucks a doll and a plant in with a blanket',
        ],
      },
      {
        descriptor:
          'Knows animals eat, drink and move on their own and toys do not, when asked about a familiar pair.',
        exemplars: [
          '"The cat eats, the teddy just pretends"',
          'Points to the animals when asked which ones are alive',
        ],
      },
      {
        descriptor:
          'Sorts eight to ten pictures into living and non-living and gives one reason for most of them.',
        exemplars: [
          '"The flower is living because it grows"',
          'Puts the chair, spoon and ball in the not-living pile',
        ],
      },
      {
        descriptor:
          'Uses more than one test — grows, needs food and water, has young, does it on its own — and handles the tricky cases by naming which test is not met.',
        exemplars: [
          '"The car moves but nobody has to feed it, and it has no babies"',
          '"The tree does not walk but it grows and drinks, so it is living"',
        ],
      },
      {
        descriptor:
          'Separates once-living things from never-living things, and sorts living things into plants and animals.',
        exemplars: [
          '"The wooden table was a tree once, but the plastic one never was"',
          'Splits the living pile into two: things that grow in the ground and things that move around',
        ],
      },
    ],
  },
  {
    key: 'lifeCycles',
    name: 'Life Cycles',
    description:
      'Living things change in a fixed order and the order comes round again: seed to plant, egg to butterfly, baby to grown-up.',
    typicalMonths: [36, 48],
    prerequisiteKeys: ['livingNonLiving'],
    assessmentHints: [
      'Shuffle four life-cycle cards and ask for them in order, then ask what comes after the last one.',
      'Keep a planted seed on the windowsill and ask each few days what is different and what is next.',
      'Ask whether a puppy changes the way a caterpillar does, and how it is different.',
    ],
    levels: [
      {
        descriptor:
          'Recognizes baby and grown-up versions of a familiar animal in pictures.',
        exemplars: [
          'Points to the puppy and the dog and says "baby, mama"',
          'Names the chick and the hen',
        ],
      },
      {
        descriptor:
          'Matches three or four baby animals to their parents, and knows a seed goes into the soil to make a plant.',
        exemplars: [
          'Pairs calf with cow, lamb with sheep, kitten with cat',
          'Pushes the bean into the pot and asks for water',
        ],
      },
      {
        descriptor:
          'Puts a plant life cycle in order from four cards and tends a planted seed, checking on it and saying what stage it is at.',
        exemplars: [
          'Orders seed, sprout, plant, flower',
          '"It is a sprout now, next it gets leaves"',
        ],
      },
      {
        descriptor:
          'Tells the butterfly cycle from memory and knows the cycle returns to the start — the butterfly lays eggs again.',
        exemplars: [
          '"Egg, caterpillar, chrysalis, butterfly, and then eggs again"',
          'Draws the four stages in a ring',
        ],
      },
      {
        descriptor:
          'Compares two life cycles and names what is the same and what is different, including cycles where the animal only grows bigger.',
        exemplars: [
          '"The frog and the butterfly both change shape, the puppy just gets bigger"',
          '"People have a cycle too — baby, kid, grown-up, and then they have babies"',
        ],
      },
    ],
  },
  {
    key: 'animalKnowledge',
    name: 'Animal Knowledge',
    description:
      'Naming animals, knowing facts about them, and grouping them by what they have in common — the first taxonomy a child builds.',
    typicalMonths: [33, 42],
    assessmentHints: [
      'Open an animal book and ask for a fact, not just the name: "what does that one do?"',
      'Ask for the animals to be put into groups any way that makes sense, then ask what the groups are.',
      'Describe an unfamiliar animal by its features and ask what kind it is.',
    ],
    levels: [
      {
        descriptor:
          'Names five or more familiar animals in a book or outdoors, and makes the sound each one makes.',
        exemplars: ['Names dog, cat, cow, duck, bear', 'Moos at the cow picture'],
      },
      {
        descriptor:
          'Names ten to fifteen animals in books and points to their parts on request.',
        exemplars: [
          'Finds the elephant’s trunk and the bird’s wing',
          'Names giraffe, zebra, penguin, frog',
        ],
      },
      {
        descriptor:
          'Names twenty or more animals, gives a fact for many of them, and sorts them into two or three groups such as farm, wild and water.',
        exemplars: [
          '"Owls fly at night and they can turn their heads round"',
          'Sorts a basket of animal figures into water animals and land animals',
        ],
      },
      {
        descriptor:
          'Uses class names — mammal, bird, fish, insect, reptile — with an example for each and a feature that defines it.',
        exemplars: [
          '"Insects have six legs, spiders have eight so they are not insects"',
          '"Birds have feathers and lay eggs"',
        ],
      },
      {
        descriptor:
          'Places an unfamiliar animal into a class from its features and explains the reasoning, and knows habitat and diet facts for many animals.',
        exemplars: [
          '"It has feathers and a beak, so it is a bird even though it cannot fly"',
          '"Polar bears live where it is icy and they eat seals"',
        ],
      },
    ],
  },
  {
    key: 'bodyKnowledge',
    name: 'How the Body Works',
    description:
      'From naming body parts to knowing what the parts inside do and how care, food and movement change them.',
    typicalMonths: [33, 42],
    assessmentHints: [
      'After running around, ask for a hand on the chest: "what is your heart doing now? why?"',
      'Ask what the tummy does with food, and what the lungs are for.',
      'Ask for a body part on the body, then for the same one on a doll or a parent.',
    ],
    levels: [
      {
        descriptor:
          'Points to five or six body parts on the body when they are named.',
        exemplars: ['Finds nose, eyes, ears, hands, feet', 'Pats the tummy when asked'],
      },
      {
        descriptor:
          'Names ten or more outside body parts, including elbow, knee and chin, on the body and on a doll.',
        exemplars: [
          'Names the parts while drawing a person',
          'Points to the doll’s elbow',
        ],
      },
      {
        descriptor:
          'Knows a few inside parts and what they do — the heart beats, lungs are for breathing, the tummy takes the food — and finds the heartbeat after running.',
        exemplars: [
          'Hand on chest after running: "it is going fast"',
          '"The food goes down to my tummy"',
        ],
      },
      {
        descriptor:
          'Connects care to the body: food gives energy, sleep grows the body, brushing keeps teeth healthy; names bones, muscles and brain with a job for each.',
        exemplars: [
          '"Bones keep me standing up and muscles pull them"',
          '"Sleep is when my body grows"',
        ],
      },
      {
        descriptor:
          'Explains a simple body system in order and links exercise to the heart beating faster because muscles need more air.',
        exemplars: [
          '"Chew, swallow, tummy, then my body takes the energy"',
          '"I ran, so my muscles wanted more air, so my heart pumped faster"',
        ],
      },
    ],
  },
]

export const SCIENCE_SKILLS: Skill[] = buildSkills('science', SCIENCE_SEEDS)

// ─────────────────────────────────────────────────────────────
// SECOND LANGUAGE
// ─────────────────────────────────────────────────────────────

export const SECOND_LANGUAGE_DOMAIN: DomainDef = buildDomain(
  'secondLanguage',
  'Second Language',
  'A family language growing alongside the home language. Understanding runs well ahead of speaking, and that gap is the healthy shape of it — the work is exposure and warmth, never pressure to perform.',
  'languages',
  12,
)

const SECOND_LANGUAGE_SEEDS: readonly SkillSeed[] = [
  {
    key: 'receptiveComprehension',
    name: 'Understanding What Is Said',
    description:
      'What lands when the second language is spoken. This grows first, grows fastest, and is the honest measure of progress in a light-exposure thread.',
    assessmentHints: [
      'Give a familiar request in the second language with hands still and no translation, then wait — hands in pockets is the whole test.',
      'Ask a yes or no question in the second language during play and accept an answer in any language.',
      'Watch during a visit: does an instruction from a relative get followed before anyone translates it?',
    ],
    levels: [
      {
        descriptor:
          'Turns toward a second-language speaker and reads the tone and the gestures; the words themselves are not doing the work yet.',
        exemplars: [
          'Looks up and smiles when a grandparent speaks Russian',
          'Comes over when called by name in an inviting tone',
        ],
      },
      {
        descriptor:
          'Follows three to five familiar one-step requests without a gesture to help.',
        exemplars: [
          'Comes over at "иди сюда" with no beckoning hand',
          'Hands the toy over at "дай мне"',
        ],
      },
      {
        descriptor:
          'Understands around fifty everyday words and follows a one-step request about an object in the room, with no gesture and no translation.',
        exemplars: [
          'Fetches the right object when asked for "the red ball" in the second language',
          'Points to the correct picture out of four when it is named',
        ],
      },
      {
        descriptor:
          'Follows two-step requests and understands short sentences about things not present, answering in the home language.',
        exemplars: [
          'Washes hands and comes to the table on one instruction',
          'Answers in English when asked in Ukrainian about lunch',
        ],
      },
      {
        descriptor:
          'Follows a story or a conversation directed at the child, answers what and where questions about it, and relays the meaning to someone else.',
        exemplars: [
          '"Babushka says put your shoes on"',
          'Laughs at the funny part of a story told in the second language, in the right place',
        ],
      },
    ],
  },
  {
    key: 'wordRepetition',
    name: 'Repeating Words',
    description:
      'Copying a word back after hearing it. Willingness matters more than accuracy — repetition is the bridge from listening to speaking.',
    assessmentHints: [
      'Name an object in the second language during play and pause invitingly; take any attempt as a yes.',
      'Say a word at breakfast and see whether it comes back later the same day when the object reappears.',
      'Offer a word with a sound the home language lacks and listen to how close the copy gets.',
    ],
    levels: [
      {
        descriptor:
          'Copies the shape of a word — its rhythm, or its last syllable — when an adult offers it warmly.',
        exemplars: ['Says "-ko" after "молоко"', 'Matches the singsong of a phrase'],
      },
      {
        descriptor:
          'Repeats a whole familiar word straight after an adult, most times, when willing.',
        exemplars: [
          'Says "мяч" after the ball is named',
          'Repeats readily during a naming game rather than turning away',
        ],
      },
      {
        descriptor:
          'Repeats a new word clearly enough to be recognized, and says it again later the same day when the object turns up.',
        exemplars: [
          'Repeats "яблоко" at lunch after hearing it at breakfast',
          'Names three new words back during one visit',
        ],
      },
      {
        descriptor:
          'Repeats three and four word phrases, and holds a word across days — offers it unprompted next time the object appears.',
        exemplars: [
          'Repeats a whole short sentence back',
          'Says a word learned on Saturday again on Monday',
        ],
      },
      {
        descriptor:
          'Copies pronunciation closely, including sounds the home language does not have, and asks for words in order to repeat them.',
        exemplars: [
          'Produces the х sound rather than substituting an h or k',
          '"How do you say cat?" then repeats the answer twice',
        ],
      },
    ],
  },
  {
    key: 'songs',
    name: 'Songs and Rhymes',
    description:
      'Songs carry a language in with the melody and no pressure attached. They are usually the largest chunk of second-language material a young child holds.',
    prerequisiteKeys: ['wordRepetition'],
    assessmentHints: [
      'Start a familiar song and stop before the last word of a line, then wait.',
      'Ask which song comes next — see whether one gets requested by name or by tune.',
      'Listen during solo play for lines from the songs turning up on their own.',
    ],
    levels: [
      {
        descriptor:
          'Moves to a second-language song — claps, bounces, lights up at the tune — without words yet.',
        exemplars: ['Bounces to the chorus', 'Asks for the song by pointing at the speaker'],
      },
      {
        descriptor:
          'Fills in the last word of a familiar line when an adult pauses for it.',
        exemplars: [
          'Supplies the rhyming word at the end of the verse',
          'Joins on the repeated chorus word',
        ],
      },
      {
        descriptor:
          'Sings the chorus of two or three second-language songs with recognizable words, and does the hand motions.',
        exemplars: [
          'Sings the chorus of a Ukrainian children’s song with the actions',
          'Keeps the melody and most of the words through one verse',
        ],
      },
      {
        descriptor:
          'Sings a whole short song from memory, asks for it by name, and knows what several of the words mean.',
        exemplars: [
          'Requests the song by its title in the second language',
          'Points to the nose on the body-parts line',
        ],
      },
      {
        descriptor:
          'Sings several songs and reuses lines from them in play, or swaps in new words — a sign the language has been taken apart, not just memorized as sound.',
        exemplars: [
          'Sings the counting song while stacking blocks',
          'Puts the dog’s name into the song where the animal word goes',
        ],
      },
    ],
  },
  {
    key: 'activeVocabulary',
    name: 'Spoken Vocabulary',
    description:
      'The words available for use in the second language. This grows long after understanding does, and that order is the healthy one.',
    prerequisiteKeys: ['receptiveComprehension', 'wordRepetition'],
    assessmentHints: [
      'Name-the-picture during a book, in the second language, and count what comes back over a week rather than in one sitting.',
      'Ask for a food or a body part in the second language at the moment it is wanted.',
      'Keep a running list on the fridge and add words as they appear naturally.',
    ],
    levels: [
      {
        descriptor:
          'Says one to three second-language words, usually names of people.',
        exemplars: ['Says "баба" when the grandmother arrives', 'Uses "дед" rather than the English word'],
      },
      {
        descriptor:
          'Says ten to twenty words when prompted — foods, animals, body parts, colors.',
        exemplars: [
          'Names four foods at the table when asked',
          'Names three animals in the book in the second language',
        ],
      },
      {
        descriptor:
          'Says thirty to fifty words on request across several topics, and offers a few of them unprompted when the object is right there.',
        exemplars: [
          'Names the fruit in the second language while it is being cut',
          'Greets a relative with the second-language word without being cued',
        ],
      },
      {
        descriptor:
          'Says a hundred or more words including verbs, and uses them in the right slot rather than only as labels.',
        exemplars: [
          'Uses the verbs for eat, sleep, give and come in the right places',
          'Names an action rather than only an object',
        ],
      },
      {
        descriptor:
          'Says two hundred or more words, reaches for the second-language word first with second-language speakers, and asks for the missing words.',
        exemplars: [
          'Switches vocabulary by who is in the room',
          '"How do you say slide?" then uses it in the next sentence',
        ],
      },
    ],
  },
  {
    key: 'phrases',
    name: 'Putting Words Together',
    description:
      'Moving from single words to two, three and four word phrases in the second language — first as memorized chunks, then built fresh.',
    prerequisiteKeys: ['activeVocabulary'],
    assessmentHints: [
      'At a moment of real wanting, model the phrase once and leave a gap for it.',
      'Notice whether a phrase is a chunk learned whole or built new — a new combination is the marker.',
      'Ask a relative to use one simple phrase all weekend and watch for it coming back.',
    ],
    levels: [
      {
        descriptor:
          'Carries a whole request with one second-language word plus a gesture, and gets understood.',
        exemplars: ['Points at the shelf and says "мама"', 'Holds a cup up and names the drink'],
      },
      {
        descriptor:
          'Says one or two fixed phrases learned whole, in the right situation.',
        exemplars: ['"спасибо" after being handed something', '"дай мне" with a hand held out for the toy'],
      },
      {
        descriptor:
          'Says a two or three word phrase when prompted, with the words in the right order.',
        exemplars: [
          '"я хочу яблоко" — I want apple — after a model',
          'Asks for more of something in a three-word phrase',
        ],
      },
      {
        descriptor:
          'Builds new three and four word phrases that were never taught as a unit, including negation and possession.',
        exemplars: [
          '"это моя книга" — this is my book',
          'Combines a verb and an object nobody has paired before',
        ],
      },
      {
        descriptor:
          'Uses sentences of five or more words with a connector, mostly correct endings, and can both ask and answer a question in the second language.',
        exemplars: [
          'Asks where something is and understands the answer',
          'Joins two ideas with "and" or "because" in the second language',
        ],
      },
    ],
  },
  {
    key: 'unpromptedSpeech',
    name: 'Speaking Without a Prompt',
    description:
      'Choosing the second language without being asked. This is the last piece to arrive, and it arrives on the child’s own schedule once enough has been heard.',
    prerequisiteKeys: ['phrases'],
    assessmentHints: [
      'During a visit, count the times a second-language word is used without anyone asking for it — zero is a fine starting count.',
      'Watch whether the language changes by person: home language with one parent, second language with a relative.',
      'Listen during pretend play — dolls are often where a new language is tried out first.',
    ],
    levels: [
      {
        descriptor:
          'Takes the language in and does not yet produce it by choice; may echo a word just heard. This quiet stage is the expected first one and often lasts months.',
        exemplars: [
          'Listens closely to a whole conversation and says nothing',
          'Echoes the last word of what a relative said',
        ],
      },
      {
        descriptor:
          'Uses a second-language word unprompted when it is the only word available for that thing.',
        exemplars: [
          'Uses the second-language name for a dish only ever named that way',
          'Calls a grandparent by the second-language word',
        ],
      },
      {
        descriptor:
          'Says a known word or phrase unprompted to a second-language speaker several times across a visit — a greeting, a thank you, a request.',
        exemplars: [
          'Greets a relative in the second language on arrival, unprompted',
          'Asks for food in the second language during the weekend',
        ],
      },
      {
        descriptor:
          'Starts short exchanges in the second language without a model several times a day when a speaker is around, and keeps the languages separate by person.',
        exemplars: [
          'Tells a grandparent about the park in the second language',
          'Switches to the home language mid-room when the other parent joins',
        ],
      },
      {
        descriptor:
          'Holds a back-and-forth in the second language by choice, picks the language by listener without being told, and tries again in another way when not understood.',
        exemplars: [
          'Carries a four-turn conversation with a relative',
          'Rephrases in the second language when the first attempt does not land',
        ],
      },
    ],
  },
]

export const SECOND_LANGUAGE_SKILLS: Skill[] = buildSkills(
  'secondLanguage',
  SECOND_LANGUAGE_SEEDS,
)

// ─────────────────────────────────────────────────────────────
// SELF CARE
// ─────────────────────────────────────────────────────────────

export const SELF_CARE_DOMAIN: DomainDef = buildDomain(
  'selfCare',
  'Self-Care & Independence',
  'Handling the body, the clothes, the food and the mess without an adult stepping in. Every step handed over is real independence, and the sequences double as fine motor and planning practice.',
  'hand-heart',
  13,
)

const SELF_CARE_SEEDS: readonly SkillSeed[] = [
  {
    key: 'toileting',
    name: 'Toileting',
    description:
      'From telling an adult afterwards, to going in time, to running the whole routine anywhere.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Notice who starts the trip to the bathroom — the child or the reminder.',
      'On an outing, see whether an unfamiliar bathroom changes anything.',
      'Watch the end of the routine: clothing, flush, hands — which parts still get done by an adult?',
    ],
    levels: [
      {
        descriptor:
          'Tells an adult after being wet, and stays dry through some naps.',
        exemplars: ['Pats the nappy and says "wet"', 'Wakes dry from a nap two days in a row'],
      },
      {
        descriptor:
          'Announces the need in time to get there, with reminders through the day, and wears underwear in the daytime.',
        exemplars: [
          'Announces it and walks toward the bathroom',
          'Dry through the morning with two reminders',
        ],
      },
      {
        descriptor:
          'Uses the toilet through the day with few accidents and only occasional reminders, managing pants down and up.',
        exemplars: [
          'A full day out with no accident',
          'Pulls leggings up unaided afterwards',
        ],
      },
      {
        descriptor:
          'Goes without being sent, including at other people’s houses, and stays dry overnight.',
        exemplars: [
          'Leaves the table unprompted and comes back',
          'Dry every night for several weeks',
        ],
      },
      {
        descriptor:
          'Runs the whole routine unaided — notices early, manages clothing, wipes, flushes, washes hands — including in a public or unfamiliar bathroom.',
        exemplars: [
          'Handles a restaurant bathroom with an adult waiting outside the stall',
          'Comes back with hands already washed',
        ],
      },
    ],
  },
  {
    key: 'handWashing',
    name: 'Hand Washing',
    description:
      'The five-step sequence — water, soap, scrub, rinse, dry — and then knowing when it is needed without being told.',
    typicalMonths: [36, 48],
    assessmentHints: [
      'Stand back at the sink and count how many steps happen before a prompt is needed.',
      'Watch the scrub itself: backs of hands and between fingers, or just palms?',
      'After the dog or the garden, wait and see whether the sink gets used unasked.',
    ],
    levels: [
      {
        descriptor:
          'Holds hands under the water and pats them on a towel while an adult does the work.',
        exemplars: ['Puts hands out under the tap', 'Takes the towel when offered'],
      },
      {
        descriptor:
          'Rubs hands together with soap when told and rinses them, with help for the taps and the drying.',
        exemplars: ['Rubs the soap between palms', 'Rinses when the hands are guided under'],
      },
      {
        descriptor:
          'Does the whole sequence — water, soap, scrub, rinse, dry — with a spoken reminder for each step.',
        exemplars: [
          'Water, soap, scrub, rinse, dry with a cue for each',
          'Reaches the tap on a step stool alone',
        ],
      },
      {
        descriptor:
          'Washes hands unprompted at the routine times and scrubs long enough — a song’s length — including the backs of the hands.',
        exemplars: [
          'Goes to the sink unprompted before eating',
          'Sings through the whole scrub',
        ],
      },
      {
        descriptor:
          'Washes whenever there is a reason and can say the reason, turns the tap off while scrubbing, and reminds others.',
        exemplars: [
          '"I touched the dog, I need soap"',
          '"You did not wash yet!" to a visitor',
        ],
      },
    ],
  },
  {
    key: 'eating',
    name: 'Eating and Utensils',
    description:
      'Self-feeding with real utensils and a real cup, then serving, cutting and clearing.',
    typicalMonths: [30, 36],
    assessmentHints: [
      'Put a shared bowl and serving spoon on the table and see who serves the plate.',
      'Offer a child knife with a banana or a pancake and watch the hand that holds it steady.',
      'Notice what happens after a spill — waiting, or fetching a cloth?',
    ],
    levels: [
      {
        descriptor:
          'Self-feeds finger foods and scoops with a spoon, with plenty landing elsewhere.',
        exemplars: ['Scoops yoghurt and gets most of it in', 'Picks up berries one at a time'],
      },
      {
        descriptor:
          'Uses a spoon and a fork with little spilling and drinks from an open cup held in two hands.',
        exemplars: ['Spears pasta with a fork', 'Drinks from an open cup without a lid'],
      },
      {
        descriptor:
          'Eats a whole meal with a fork and spoon and drinks from an open cup one-handed, staying reasonably tidy.',
        exemplars: [
          'Finishes a full meal without help',
          'Sets the cup down without tipping it',
        ],
      },
      {
        descriptor:
          'Uses a child knife on soft food, serves from a shared bowl, and wipes up spills.',
        exemplars: [
          'Cuts a banana into rounds with a child knife',
          'Spoons rice from the middle dish onto the plate',
        ],
      },
      {
        descriptor:
          'Manages a full place setting including a table knife for spreading and cutting, serves others, and carries the plate to the sink.',
        exemplars: [
          'Spreads butter on toast and cuts it in half',
          'Offers the bowl to someone else before taking a share',
        ],
      },
    ],
  },
  {
    key: 'dressing',
    name: 'Dressing',
    description:
      'Getting clothes on and off, then getting them on the right way round, then choosing them.',
    typicalMonths: [30, 42],
    assessmentHints: [
      'Lay clothes out and leave the room for two minutes — what is on when you come back?',
      'Hand over a shirt inside out and see whether it gets turned.',
      'Ask what to wear before going out on a cold day and listen for the weather reasoning.',
    ],
    levels: [
      {
        descriptor:
          'Pushes an arm through a held sleeve and pulls off easy items — hat, socks, unfastened shoes.',
        exemplars: ['Pulls both socks off', 'Pushes an arm through when the sleeve is held open'],
      },
      {
        descriptor:
          'Takes off most clothes alone and puts on loose items with help lining them up.',
        exemplars: [
          'Steps into pants held open and pulls them up',
          'Takes off a t-shirt without help',
        ],
      },
      {
        descriptor:
          'Puts on and takes off most clothes alone, with help only for fasteners and for front-versus-back.',
        exemplars: [
          'Dresses in leggings and a t-shirt unaided',
          'Asks which way round the shirt goes',
        ],
      },
      {
        descriptor:
          'Dresses fully unaided, including turning clothes right side out, and picks weather-appropriate clothes when asked.',
        exemplars: [
          'Turns an inside-out sleeve back through',
          'Chooses a jumper because it is cold out',
        ],
      },
      {
        descriptor:
          'Chooses a whole outfit for the weather and the plan, manages buttons and zips, and gets dressed in the morning without step-by-step prompts.',
        exemplars: [
          'Lays out clothes the night before a trip',
          'Does the buttons on a cardigan',
        ],
      },
    ],
  },
  {
    key: 'shoesOn',
    name: 'Shoes On',
    description:
      'Shoes on the right feet, fastened, and eventually laced — a small daily test of body mapping and patience.',
    typicalMonths: [33, 42],
    prerequisiteKeys: ['dressing'],
    assessmentHints: [
      'Put both shoes down in a jumble and see whether they get sorted before they get worn.',
      'Ask "do they feel right?" after they are on, and see whether the answer comes from the feet.',
      'Watch the velcro or buckle: fastened fully, or left flapping?',
    ],
    levels: [
      {
        descriptor:
          'Pushes a foot into a shoe held open by an adult, and pulls shoes off alone.',
        exemplars: ['Takes both shoes off at the door', 'Pushes the foot in when the heel is held'],
      },
      {
        descriptor:
          'Puts slip-on shoes on unaided, often swapped left and right, with help getting the heel in.',
        exemplars: ['Gets both shoes on unaided but on the wrong feet', 'Asks for help with the heel'],
      },
      {
        descriptor:
          'Puts shoes on and gets them on the correct feet most times, using a cue such as a sticker or the curve between the toes, and fastens velcro.',
        exemplars: [
          'Lines the shoes up so the curves make a smile before stepping in',
          'Presses both velcro straps down',
        ],
      },
      {
        descriptor:
          'Gets shoes on the right feet without a cue, fastens buckles or velcro neatly, and checks them before going out.',
        exemplars: [
          'Looks down, swaps them, and says "now they are right"',
          'Does a buckle unaided',
        ],
      },
      {
        descriptor:
          'Manages laces to a knot or a bow with a little help, chooses shoes to suit the weather and the plan, and puts them back in their place.',
        exemplars: [
          'Ties the first knot alone and asks for help with the bow',
          'Picks boots because of the rain',
        ],
      },
    ],
  },
  {
    key: 'jacketZipping',
    name: 'Jackets and Zips',
    description:
      'The hardest everyday fastener: joining the zip at the bottom takes two hands doing different jobs at once.',
    typicalMonths: [42, 54],
    prerequisiteKeys: ['dressing'],
    assessmentHints: [
      'Join the zip and hand it over — does the pull go all the way to the top?',
      'Leave the zip unjoined and watch the two hands work on the bottom.',
      'At the end of an outing, see whether the jacket comes off and gets hung up unaided.',
    ],
    levels: [
      {
        descriptor:
          'Pushes both arms into a held jacket and pulls at the zip pull without moving it far.',
        exemplars: ['Gets both arms in when the jacket is held', 'Tugs the zip pull down'],
      },
      {
        descriptor:
          'Puts a jacket on by the flip-over-the-head method and pulls the zip up once an adult has joined it at the bottom.',
        exemplars: [
          'Flips the coat over the head and lands both arms in',
          'Pulls the joined zip to the top',
        ],
      },
      {
        descriptor:
          'Puts the jacket on unaided and zips it all the way up once it has been started, and unzips it alone.',
        exemplars: [
          'Zips from the bottom to the chin after a start',
          'Unzips and hangs the coat on a low hook',
        ],
      },
      {
        descriptor:
          'Joins the zip unaided — sets the pin into the box at the bottom — and zips up, sometimes after a few tries.',
        exemplars: [
          'Gets the pin seated on the third attempt and zips up',
          'Holds the bottom steady with one hand while pulling with the other',
        ],
      },
      {
        descriptor:
          'Zips and unzips quickly and reliably, manages buttons and snaps, and gets ready to go out without help.',
        exemplars: [
          'Coat, hat and shoes on in one go while an adult finds keys',
          'Does the snaps at the collar',
        ],
      },
    ],
  },
  {
    key: 'teethBrushing',
    name: 'Teeth Brushing',
    description:
      'Brushing all the surfaces for long enough, twice a day, and eventually without being started by anyone.',
    typicalMonths: [36, 48],
    assessmentHints: [
      'Let the brushing happen first and finish afterwards — notice which surfaces were reached.',
      'Play a two-minute song and see whether the brushing lasts the song.',
      'Notice who starts the routine at night: the child, or the reminder.',
    ],
    levels: [
      {
        descriptor:
          'Opens up for an adult to brush, chews on the brush in turn, and knows where brushing sits in the bedtime order.',
        exemplars: ['Fetches the toothbrush at bath time', 'Opens wide when asked'],
      },
      {
        descriptor:
          'Brushes some surfaces alone for a short time, and an adult finishes the job.',
        exemplars: ['Brushes the front teeth then hands the brush over', 'Spits into the sink'],
      },
      {
        descriptor:
          'Brushes for a whole song reaching top, bottom, front and back, with an adult finishing and helping with the toothpaste.',
        exemplars: [
          'Gets the back teeth on both sides',
          'Spits rather than swallowing the toothpaste',
        ],
      },
      {
        descriptor:
          'Brushes twice a day with only a reminder to start, squeezes a pea of toothpaste, rinses and puts the brush away; an adult checks rather than redoes.',
        exemplars: [
          'Puts the toothpaste on unaided without a flood',
          'Rinses the brush and stands it up',
        ],
      },
      {
        descriptor:
          'Runs the whole routine morning and night unprompted, times the two minutes, flosses with help, and notices when it has been missed.',
        exemplars: [
          '"We forgot my teeth last night" the next morning',
          'Sets the timer unaided and brushes till it goes',
        ],
      },
    ],
  },
  {
    key: 'pouring',
    name: 'Pouring',
    description:
      'Wrist control, aim and judgement in one action — the classic practical-life exercise, and the one that makes a child useful at the table.',
    typicalMonths: [33, 42],
    assessmentHints: [
      'Put a small jug half full on the table at a meal and let it be used.',
      'Ask for a pour for someone else and watch the care level change.',
      'Spill a little on purpose and see who fetches the cloth.',
    ],
    levels: [
      {
        descriptor:
          'Tips a container to empty it in play — water, sand, blocks — with no aim at a target.',
        exemplars: ['Empties one bucket into another in the bath', 'Tips out the block bin'],
      },
      {
        descriptor:
          'Pours dry goods between two small jugs with most of it landing inside.',
        exemplars: ['Pours rice from jug to jug with a few grains lost', 'Uses two hands on the handle'],
      },
      {
        descriptor:
          'Pours water from a small pitcher into a cup without overfilling, and stops when the cup is full.',
        exemplars: [
          'Pours a cup of water at lunch from a small pitcher',
          'Stops at the line without being told',
        ],
      },
      {
        descriptor:
          'Pours from a heavier jug with two hands, pours to a set line, and pours for another person carefully without being asked to be careful.',
        exemplars: [
          'Fills a guest’s glass to the same level as the others',
          'Measures to the mark for a recipe',
        ],
      },
      {
        descriptor:
          'Pours from a full family-size jug, judges what is left, and clears a spill without prompting.',
        exemplars: [
          '"There is not enough for two cups"',
          'Fetches the cloth and wipes before anyone reacts',
        ],
      },
    ],
  },
  {
    key: 'foodPrep',
    name: 'Making Food',
    description:
      'Real jobs in the kitchen: washing, tearing, spreading, cutting soft food, and following a short recipe.',
    typicalMonths: [36, 48],
    prerequisiteKeys: ['eating', 'pouring'],
    assessmentHints: [
      'Set out the pieces of a snack and ask for it to be made, then stand back.',
      'Give a three-picture recipe and see whether the steps run in order without narration.',
      'Ask what the rules are in the kitchen before starting — hot, sharp, wash hands.',
    ],
    levels: [
      {
        descriptor:
          'Watches, hands over ingredients, and tips in a portion someone else has measured.',
        exemplars: ['Tips the pre-measured flour into the bowl', 'Passes the egg over when asked'],
      },
      {
        descriptor:
          'Stirs a bowl, washes fruit under the tap and tears lettuce, with plenty of enthusiasm and some of it on the counter.',
        exemplars: ['Rinses grapes in the colander', 'Stirs batter without tipping the bowl'],
      },
      {
        descriptor:
          'Does two or three real steps of a snack — washes and tears, spreads with a child knife, sprinkles a topping — and eats what came out of it.',
        exemplars: [
          'Spreads cream cheese on a cracker and adds cucumber',
          'Washes and tears the salad for the table',
        ],
      },
      {
        descriptor:
          'Makes a simple snack end to end once the food is set out, and clears the board afterwards.',
        exemplars: [
          'Peels a mandarin, cuts a banana and plates both',
          'Puts the board and knife in the sink when finished',
        ],
      },
      {
        descriptor:
          'Follows a four or five step picture recipe with an adult nearby, measures a cup or a spoon, and states the safety rules before starting.',
        exemplars: [
          'Cracks an egg into a bowl and fishes out a shell piece',
          '"Wash hands first, and the oven is hot so grown-ups only"',
        ],
      },
    ],
  },
  {
    key: 'tidying',
    name: 'Tidying Up',
    description:
      'Putting things back where they live, finishing the job, and eventually starting it without being asked. It is as much planning as it is willingness.',
    typicalMonths: [36, 48],
    assessmentHints: [
      'At the end of an activity, give one start prompt and then watch how far the job runs.',
      'Ask "where does this live?" for three different objects.',
      'Offer a two-part job and see whether it gets split up out loud.',
    ],
    levels: [
      {
        descriptor:
          'Puts one object into a container when handed it and shown where it goes.',
        exemplars: ['Drops a block in the basket when passed it', 'Carries a cup to the sink when asked'],
      },
      {
        descriptor:
          'Tidies alongside an adult who does most of the work, and responds to a single clear instruction.',
        exemplars: ['Puts the blocks in the basket while an adult clears the table', 'Fills the toy bin together'],
      },
      {
        descriptor:
          'Puts a set of toys away after an activity with a prompt to start, and knows where several things belong.',
        exemplars: [
          'Clears the whole puzzle back into its box',
          'Takes clothes to the hamper unaided',
        ],
      },
      {
        descriptor:
          'Tidies the space used as part of the routine, sorts things to their right places, and owns one or two regular jobs.',
        exemplars: [
          'Sets the table every night as an owned job',
          'Sorts the animals and the cars into different bins',
        ],
      },
      {
        descriptor:
          'Notices mess and deals with it without being asked, finishes the small pieces too, and helps plan a bigger clear-up by splitting the work.',
        exemplars: [
          '"I will do the books, you do the train track"',
          'Picks up the last few beads without being reminded',
        ],
      },
    ],
  },
]

export const SELF_CARE_SKILLS: Skill[] = buildSkills('selfCare', SELF_CARE_SEEDS)

// ─────────────────────────────────────────────────────────────
// Bundles
// ─────────────────────────────────────────────────────────────

export const TIME_SCIENCE_LANGUAGE_SELFCARE_DOMAINS: DomainDef[] = [
  TIME_DOMAIN,
  SCIENCE_DOMAIN,
  SECOND_LANGUAGE_DOMAIN,
  SELF_CARE_DOMAIN,
]

export const TIME_SCIENCE_LANGUAGE_SELFCARE_SKILLS: Skill[] = [
  ...TIME_SKILLS,
  ...SCIENCE_SKILLS,
  ...SECOND_LANGUAGE_SKILLS,
  ...SELF_CARE_SKILLS,
]
